import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Heart, Star, ShoppingBag, Send, Loader2, Check, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import ImmersiveLayout from "../components/ImmersiveLayout";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { useWishlist } from "../contexts/WishlistContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

export default function ProductDetailPage() {
  const { productId } = useParams();
  const { language } = useLanguage();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const { isWishlisted, toggleWishlist } = useWishlist();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ avg_rating: 0, rating_count: 0, distribution: {} });
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewLoading, setReviewLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);

  // Variant selection
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [showVariants, setShowVariants] = useState(false);

  const wishlisted = isWishlisted(productId);
  const id = language === "id";

  useEffect(() => {
    setLoading(true);
    setSelectedVariantId(null);
    axios.get(`${API}/products/${productId}`)
      .then(r => {
        setProduct(r.data);
        // Auto-select default variant
        const variants = r.data.variants || [];
        const def = variants.find(v => v.is_default && v.status === "active") || variants.find(v => v.status === "active");
        if (def) setSelectedVariantId(def.variant_id);
      })
      .catch(() => navigate("/catalog"))
      .finally(() => setLoading(false));
    axios.get(`${API}/products/${productId}/reviews`)
      .then(r => {
        setReviews(r.data.reviews || []);
        setReviewStats({ avg_rating: r.data.avg_rating, rating_count: r.data.rating_count, distribution: r.data.distribution });
      }).catch(() => {});
  }, [productId, navigate]);

  const activeVariants = useMemo(() => {
    if (!product?.variants) return [];
    return product.variants.filter(v => v.status !== "inactive");
  }, [product]);

  const selectedVariant = useMemo(() => {
    if (!selectedVariantId || !activeVariants.length) return null;
    return activeVariants.find(v => v.variant_id === selectedVariantId) || null;
  }, [selectedVariantId, activeVariants]);

  const displayPrice = selectedVariant?.price ?? product?.price ?? 0;

  // Switch to variant-specific images when variant is selected
  const displayImages = useMemo(() => {
    if (selectedVariant?.images?.length) return selectedVariant.images;
    if (!product) return [];
    return product.images?.length ? product.images : [product.image_url].filter(Boolean);
  }, [selectedVariant, product]);

  const handleAddToCart = async () => {
    if (activeVariants.length > 0 && !selectedVariantId) {
      setShowVariants(true);
      toast.info(id ? "Pilih varian terlebih dahulu" : "Please select a variant first");
      return;
    }
    const opts = selectedVariantId ? { variant_id: selectedVariantId } : {};
    await addToCart(product.product_id, 1, [], opts);
    toast.success(id ? "Ditambahkan ke keranjang" : "Added to cart");
  };

  const handleWishlist = async () => {
    if (!user) { toast.info(id ? "Masuk untuk menyimpan favorit" : "Sign in to save favorites"); return; }
    await toggleWishlist(product.product_id);
  };

  const handleSubmitReview = async () => {
    if (!user) { toast.info(id ? "Masuk untuk menulis ulasan" : "Sign in to write a review"); return; }
    setReviewLoading(true);
    try {
      const res = await axios.post(`${API}/products/${productId}/reviews`, { rating: reviewRating, comment: reviewComment }, { withCredentials: true });
      setReviews(prev => [res.data, ...prev]);
      setReviewStats(prev => ({
        ...prev,
        avg_rating: ((prev.avg_rating * prev.rating_count) + reviewRating) / (prev.rating_count + 1),
        rating_count: prev.rating_count + 1
      }));
      setShowReviewForm(false);
      setReviewComment("");
      toast.success(id ? "Ulasan berhasil dikirim!" : "Review submitted!");
    } catch (err) {
      toast.error(err.response?.data?.detail || (id ? "Gagal mengirim ulasan" : "Failed to submit review"));
    }
    setReviewLoading(false);
  };

  // Auto-reset image index when variant images change
  useEffect(() => { setSelectedImage(0); }, [selectedVariantId]);

  if (loading) {
    return (
      <ImmersiveLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="animate-spin text-seikat-pink" size={32} />
        </div>
      </ImmersiveLayout>
    );
  }
  if (!product) return null;

  const name = (id ? product.name_id : product.name_en) || product.name || product.name_en || product.name_id;
  const desc = (id ? product.description_id : product.description_en) || product.description || "";
  const story = (id ? product.story_id : product.story_en) || product.story || "";
  const images = displayImages.length ? displayImages : ["https://images.unsplash.com/photo-1551468220-0a25172193f9?w=1200"];
  const hasVariants = activeVariants.length > 0;

  // Price range for products with variants
  const variantPrices = activeVariants.map(v => v.price).filter(Boolean);
  const minPrice = variantPrices.length ? Math.min(...variantPrices) : product.price || 0;
  const maxPrice = variantPrices.length ? Math.max(...variantPrices) : product.price || 0;
  const hasPriceRange = variantPrices.length > 1 && minPrice !== maxPrice && !selectedVariant;

  return (
    <ImmersiveLayout>
      {/* HERO */}
      <section className="relative min-h-[100vh] pt-20">
        <div className="absolute inset-0">
          <img src={images[selectedImage] || images[0]} alt={name} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-seikat-ink/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-seikat-ink via-seikat-ink/40 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-16 pt-12 pb-32 grid lg:grid-cols-12 gap-12 min-h-[100vh] items-end">
          <button onClick={() => navigate(-1)} className="absolute top-6 left-6 lg:top-10 lg:left-16 flex items-center gap-2 text-seikat-bone/80 hover:text-seikat-pink transition-colors text-sm font-body" data-testid="back-btn" data-magnet>
            <ArrowLeft size={16} /> {id ? "Kembali" : "Back"}
          </button>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE }}
            className="lg:col-span-8 space-y-7"
          >
            <div className="flex items-center gap-3">
              <span className="w-8 h-px bg-seikat-pink" />
              <span className="eyebrow-immersive">{product.occasion_tags?.[0] || product.style_tags?.[0] || "Signature"}</span>
              {reviewStats.rating_count > 0 && (
                <span className="flex items-center gap-1 text-seikat-bone-muted text-xs ml-2">
                  <Star size={12} fill="#C5DB7B" className="text-seikat-lime" />
                  {reviewStats.avg_rating?.toFixed(1)} ({reviewStats.rating_count})
                </span>
              )}
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-seikat-bone leading-[0.95] tracking-tight" data-testid="product-name">
              {name}
            </h1>
            {desc && <p className="font-body text-seikat-bone/75 text-lg max-w-2xl font-light leading-relaxed">{desc}</p>}

            {/* Variant Selector */}
            {hasVariants && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: EASE }}
                className="space-y-4"
                data-testid="variant-selector"
              >
                {/* Sizes */}
                {[...new Set(activeVariants.map(v => v.size).filter(Boolean))].length > 0 && (
                  <div>
                    <p className="eyebrow-immersive text-seikat-bone-muted mb-3">{id ? "Ukuran" : "Size"}</p>
                    <div className="flex flex-wrap gap-2">
                      {[...new Set(activeVariants.map(v => v.size).filter(Boolean))].map(size => {
                        const available = activeVariants.filter(v => v.size === size && (selectedVariant?.color ? v.color === selectedVariant.color : true));
                        const isSelected = selectedVariant?.size === size;
                        return (
                          <button
                            key={size}
                            onClick={() => {
                              const match = activeVariants.find(v => v.size === size && (!selectedVariant?.color || v.color === selectedVariant.color)) || activeVariants.find(v => v.size === size);
                              if (match) setSelectedVariantId(match.variant_id);
                            }}
                            data-testid={`variant-size-${size}`}
                            className={`px-4 py-2 rounded-full border text-sm font-body tracking-wide transition-all duration-200 ${
                              isSelected
                                ? "border-seikat-pink bg-seikat-pink/15 text-seikat-pink"
                                : "border-white/20 text-seikat-bone/70 hover:border-seikat-pink/50 hover:text-seikat-bone"
                            } ${available.length === 0 ? "opacity-40 cursor-not-allowed" : ""}`}
                          >
                            {size}
                            {isSelected && <Check size={10} className="inline ml-1" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Colors */}
                {[...new Set(activeVariants.map(v => v.color).filter(Boolean))].length > 0 && (
                  <div>
                    <p className="eyebrow-immersive text-seikat-bone-muted mb-3">{id ? "Warna" : "Color"}</p>
                    <div className="flex flex-wrap gap-2">
                      {[...new Set(activeVariants.map(v => v.color).filter(Boolean))].map(color => {
                        const match = activeVariants.find(v => v.color === color && (!selectedVariant?.size || v.size === selectedVariant.size)) || activeVariants.find(v => v.color === color);
                        const isSelected = selectedVariant?.color === color;
                        return (
                          <button
                            key={color}
                            onClick={() => match && setSelectedVariantId(match.variant_id)}
                            data-testid={`variant-color-${color}`}
                            className={`px-4 py-2 rounded-full border text-sm font-body tracking-wide transition-all duration-200 ${
                              isSelected
                                ? "border-seikat-lime bg-seikat-lime/15 text-seikat-lime"
                                : "border-white/20 text-seikat-bone/70 hover:border-seikat-lime/50 hover:text-seikat-bone"
                            }`}
                          >
                            {color}
                            {isSelected && <Check size={10} className="inline ml-1" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Stock info */}
                {selectedVariant && selectedVariant.track_stock && (
                  <p className="font-body text-xs text-seikat-bone-muted" data-testid="variant-stock">
                    {selectedVariant.stock > 0
                      ? (id ? `Stok tersedia: ${selectedVariant.stock}` : `In stock: ${selectedVariant.stock}`)
                      : (id ? "Stok habis" : "Out of stock")}
                  </p>
                )}
              </motion.div>
            )}

            {/* Price + CTAs */}
            <div className="flex flex-wrap items-center gap-3 pt-4">
              <div>
                <span className="font-heading text-3xl text-seikat-bone" data-testid="product-price">
                  Rp {displayPrice?.toLocaleString("id-ID")}
                </span>
                {/* Show price range hint even when variant is auto-selected */}
                {variantPrices.length > 1 && minPrice !== maxPrice && selectedVariant && (
                  <p className="font-body text-xs text-seikat-bone-muted mt-0.5" data-testid="price-range-hint">
                    {id ? `Mulai dari Rp ${minPrice?.toLocaleString("id-ID")}` : `Starting from Rp ${minPrice?.toLocaleString("id-ID")}`}
                  </p>
                )}
                {hasPriceRange && (
                  <p className="font-body text-xs text-seikat-bone-muted mt-0.5">
                    {id ? "Pilih ukuran/warna untuk melihat harga" : "Select size/color for exact price"}
                  </p>
                )}
              </div>
              {selectedVariant?.sku && (
                <span className="font-body text-xs text-seikat-bone-muted tracking-widest">SKU: {selectedVariant.sku}</span>
              )}
              <button
                onClick={handleAddToCart}
                disabled={selectedVariant?.track_stock && selectedVariant?.stock === 0}
                className="btn-pill btn-pill--solid disabled:opacity-40"
                data-testid="product-add-cart"
                data-magnet
              >
                <ShoppingBag size={14} /> {id ? "Tambah ke Keranjang" : "Add to Cart"}
              </button>
              <button onClick={handleWishlist} className="btn-pill" data-testid="product-wishlist" data-magnet>
                <Heart size={14} fill={wishlisted ? "#D4859B" : "none"} color={wishlisted ? "#D4859B" : "currentColor"} />
                {wishlisted ? (id ? "Tersimpan" : "Saved") : (id ? "Simpan" : "Save")}
              </button>
            </div>

            {/* Variant-specific image indicator */}
            {selectedVariant?.images?.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-seikat-pink/15 border border-seikat-pink/30 w-fit"
                data-testid="variant-images-active">
                <span className="w-2 h-2 rounded-full bg-seikat-pink animate-pulse" />
                <span className="font-body text-[10px] tracking-widest uppercase text-seikat-pink">
                  {id ? `Foto varian: ${[selectedVariant.size, selectedVariant.color].filter(Boolean).join(" / ")}` : `Photos for: ${[selectedVariant.size, selectedVariant.color].filter(Boolean).join(" / ")}`}
                </span>
              </motion.div>
            )}

            {/* Image gallery thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-2 pt-2">
                {images.slice(0, 5).map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImage(i)}
                    className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === i ? "border-seikat-pink" : "border-white/10 hover:border-white/30"
                    }`}
                    data-testid={`product-thumb-${i}`}
                  >
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* STORY */}
      {story && (
        <section className="relative py-32 px-6 lg:px-16">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <span className="w-8 h-px bg-seikat-lime" />
              <span className="eyebrow-immersive">{id ? "Cerita Bouquet" : "The Story"}</span>
            </div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1, ease: EASE }}
              className="font-heading text-3xl sm:text-4xl lg:text-5xl text-seikat-bone leading-[1.15] italic"
            >
              "{story}"
            </motion.p>
          </div>
        </section>
      )}

      {/* GALLERY */}
      {images.length > 1 && (
        <section className="relative py-16 px-6 lg:px-16">
          <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6">
            {images.slice(1, 7).map((src, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                className={`relative overflow-hidden rounded-lg cursor-pointer ${ i % 3 === 1 ? "aspect-[3/4]" : "aspect-square"}`}
                onClick={() => setSelectedImage(i + 1)}
              >
                <img src={src} alt={`${name} ${i + 2}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 ease-out" />
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* REVIEWS */}
      <section className="relative py-32 px-6 lg:px-16">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="w-8 h-px bg-seikat-pink" />
                <span className="eyebrow-immersive">{id ? "Ulasan" : "Reviews"}</span>
              </div>
              <h2 className="font-heading text-4xl lg:text-5xl text-seikat-bone">
                {reviewStats.rating_count > 0 ? (
                  <>{reviewStats.avg_rating?.toFixed(1)} <em className="text-seikat-pink">★</em> <span className="text-seikat-bone-muted text-2xl ml-2">({reviewStats.rating_count})</span></>
                ) : (
                  <em>{id ? "Jadilah yang pertama bercerita" : "Be the first to share"}</em>
                )}
              </h2>
            </div>
            {user && !showReviewForm && (
              <button onClick={() => setShowReviewForm(true)} className="btn-pill" data-testid="open-review-form" data-magnet>
                {id ? "Tulis Ulasan" : "Write a Review"}
              </button>
            )}
          </div>

          {showReviewForm && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-dark rounded-2xl p-6 mb-10"
            >
              <div className="flex items-center gap-2 mb-4">
                {[1, 2, 3, 4, 5].map(n => (
                  <button key={n} onClick={() => setReviewRating(n)} aria-label={`${n} stars`} data-testid={`review-star-${n}`} className="transition-transform hover:scale-110">
                    <Star size={26} fill={n <= reviewRating ? "#C5DB7B" : "none"} color={n <= reviewRating ? "#C5DB7B" : "#A6A199"} />
                  </button>
                ))}
              </div>
              <textarea
                value={reviewComment}
                onChange={e => setReviewComment(e.target.value)}
                placeholder={id ? "Ceritakan pengalamanmu..." : "Share your experience..."}
                rows={3}
                data-testid="review-comment-input"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-seikat-bone placeholder:text-seikat-bone-muted/70 font-body text-sm outline-none focus:border-seikat-pink/60 resize-none"
              />
              <div className="flex justify-end gap-2 mt-3">
                <button onClick={() => setShowReviewForm(false)} className="btn-pill" data-testid="review-cancel" data-magnet>{id ? "Batal" : "Cancel"}</button>
                <button onClick={handleSubmitReview} disabled={reviewLoading} className="btn-pill btn-pill--solid disabled:opacity-50" data-testid="review-submit" data-magnet>
                  {reviewLoading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  {id ? "Kirim" : "Send"}
                </button>
              </div>
            </motion.div>
          )}

          {reviews.length > 0 ? (
            <div className="grid sm:grid-cols-2 gap-6">
              {reviews.map((r, i) => (
                <motion.div
                  key={r.review_id || i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className="glass-dark rounded-2xl p-6"
                  data-testid={`review-card-${i}`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    {Array.from({ length: 5 }).map((_, n) => (
                      <Star key={n} size={12} fill={n < r.rating ? "#C5DB7B" : "none"} color={n < r.rating ? "#C5DB7B" : "#A6A199"} />
                    ))}
                  </div>
                  {r.comment && <p className="font-body text-seikat-bone/85 text-sm leading-relaxed italic">"{r.comment}"</p>}
                  <div className="flex items-center justify-between mt-3">
                    <p className="font-body text-seikat-bone-muted text-xs tracking-wide">— {r.user_name || "Customer"}</p>
                    {r.is_verified_purchase && (
                      <span className="flex items-center gap-1 text-[#C5DB7B] text-[10px] font-body tracking-wide">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                        {id ? "Pembelian Terverifikasi" : "Verified Purchase"}
                      </span>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          ) : !showReviewForm && (
            <p className="font-body text-seikat-bone-muted text-center py-8 italic">{id ? "Belum ada ulasan untuk bouquet ini." : "No reviews yet for this bouquet."}</p>
          )}
        </div>
      </section>

      <footer className="border-t border-white/10 mt-16 py-12 px-6 lg:px-16 pb-32">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between gap-6 items-start md:items-center">
          <div className="font-heading text-3xl text-seikat-bone">Seikat<span className="text-seikat-pink">.</span></div>
          <div className="font-body text-xs tracking-widest text-seikat-bone-muted uppercase">© 2026 · Jakarta, Indonesia</div>
        </div>
      </footer>
    </ImmersiveLayout>
  );
}
